"use client";

import { Fragment, useCallback, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";
import { useOverlayState } from "@/lib/debug/overlay-registry";
import { cn } from "@/lib/utils";
import { LiveClock } from "./components/live-clock";
import { DebugPanelTabContent } from "./debug-panel-tab-content";
import {
  DEBUG_PANEL_VERSION,
  type DebugSessionContext,
  type SafeDebugOrganization,
  type DebugTab,
  type RouteManifest,
} from "./debug-panel.types";
import { useDebugDrag } from "./hooks/use-debug-drag";
import { useIsMounted } from "./hooks/use-is-mounted";
import { useTabNoticeState } from "./hooks/use-tab-notice-state";
import { useViewportInfo } from "./hooks/use-viewport-info";
import { ExportButton } from "./panels/export-button";
import { addNavEntry } from "./stores/nav-history-store";

const DEBUG_TABS = [
  { key: "visual", group: 0 },
  { key: "files", group: 0 },
  { key: "network", group: 1 },
  { key: "queries", group: 1 },
  { key: "session", group: 1 },
  { key: "sse", group: 1 },
  { key: "errors", group: 2 },
  { key: "i18n", group: 2 },
  { key: "permissions", group: 3 },
  { key: "forms", group: 3 },
  { key: "nav", group: 3 },
] as const satisfies readonly { key: DebugTab; group: number }[];

// ── DebugPanel ────────────────────────────────────────────────────────────────

export function DebugPanel() {
  const t = useTranslations("debug");
  const pathname = usePathname();
  const user = null;
  const selectedOrganization = null;
  const organizations: SafeDebugOrganization[] = [];
  const authIsLoading = false;
  const authError = null;
  const isCentralAuthPage = pathname === "/access";
  const sessionContext: DebugSessionContext = {
    scope: isCentralAuthPage ? "central" : "tenant",
    label: isCentralAuthPage
      ? t("session.centralBoundary")
      : t("session.tenantBoundary"),
    state: "unknown",
  };
  const isRTL = false;

  const [showDebug, setShowDebug] = useState(false);
  const mounted = useIsMounted();
  const [activeTab, setActiveTab] = useState<DebugTab>("visual");
  const [manifest, setManifest] = useState<RouteManifest | null>(null);
  const overlays = useOverlayState();
  const viewport = useViewportInfo();
  const { containerRef, pos, didDrag, dragHandleProps } = useDebugDrag();

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      void import("@/lib/debug/route-manifest.json").then((m) =>
        setManifest(m.default as RouteManifest),
      );
    }
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      addNavEntry(pathname);
    }
  }, [pathname]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        setShowDebug((prev) => !prev);
      } else if (e.key === "Escape" && showDebug) {
        e.preventDefault();
        setShowDebug(false);
      }
    },
    [showDebug],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const tabNotices = useTabNoticeState();

  const panelContent = (
    <div
      ref={containerRef}
      data-feedback-capture-exclude
      className={cn(
        "fixed z-[2147483646]",
        !pos && (isRTL ? "left-4 bottom-4" : "right-4 bottom-4"),
      )}
      style={
        pos
          ? {
              left: pos.x,
              top: pos.y,
              right: "auto",
              bottom: "auto",
              zIndex: 2147483646,
              pointerEvents: "auto",
            }
          : { zIndex: 2147483646, pointerEvents: "auto" }
      }
    >
      {/* Floating Trigger Button */}
      <button
        {...dragHandleProps}
        onClick={() => {
          if (!didDrag.current) setShowDebug((prev) => !prev);
        }}
        className="bg-amber-500 text-slate-950 hover:bg-amber-400 px-3.5 py-1.5 rounded-md text-sm font-semibold shadow-lg shadow-amber-500/20 border border-amber-400 cursor-grab active:cursor-grabbing select-none touch-none transition-colors"
        title={t("triggerTitle")}
      >
        {showDebug ? t("close") : t("triggerOpen")}
      </button>

      {/* Main Panel Window */}
      {showDebug && (
        <div
          role="dialog"
          aria-label={t("panelLabel")}
          className="mt-2 bg-background border border-border rounded-xl shadow-2xl w-[min(58rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] max-h-[82vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 sm:px-5 py-2.5 border-b border-border bg-muted/20 cursor-grab active:cursor-grabbing select-none touch-none shrink-0"
            {...dragHandleProps}
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-foreground flex items-center justify-center shrink-0">
                <span className="text-background font-bold text-sm">
                  {t("mark")}
                </span>
              </div>
              <span className="text-sm font-semibold tracking-tight">
                {t("headerTitle")}
              </span>
              <span className="px-1.5 py-0.2 rounded text-sm font-mono border border-border bg-muted text-muted-foreground shrink-0 font-medium">
                {DEBUG_PANEL_VERSION}
              </span>
              {viewport.mounted && (
                <span
                  className="px-1.5 py-0.2 rounded text-sm font-mono border border-border/80 bg-muted/60 text-muted-foreground shrink-0 font-medium hidden xs:inline-flex items-center gap-1"
                  title={t("viewportTitle")}
                >
                  <span>
                    {"📐 "}
                    {viewport.width}
                    {"×"}
                    {viewport.height}
                  </span>
                  <span className="font-semibold text-foreground">
                    {"("}
                    {viewport.breakpoint}
                    {")"}
                  </span>
                </span>
              )}
              <span className="hidden sm:block text-sm text-muted-foreground/60 truncate max-w-[200px]">
                {pathname}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <ExportButton pathname={pathname} />
              <kbd className="hidden md:inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border bg-muted text-sm text-muted-foreground font-mono">
                {t("shortcutKey")}
              </kbd>
            </div>
          </div>

          {/* Tab bar */}
          <div className="flex items-center gap-1 px-3 py-2 shrink-0 overflow-x-auto min-w-0 max-w-full border-b border-border/60 bg-muted/20 scrollbar-thin">
            {DEBUG_TABS.map((tab, idx, arr) => {
              const notice = tabNotices[tab.key];
              return (
                <Fragment key={tab.key}>
                  {idx > 0 && arr[idx - 1]?.group !== tab.group && (
                    <div className="w-px h-3.5 bg-border/60 mx-1 shrink-0" />
                  )}
                  <button
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      "shrink-0 flex items-center gap-1.5 px-2.5 py-1 text-sm font-mono font-medium transition-all whitespace-nowrap rounded-lg cursor-pointer z-10 relative",
                      activeTab === tab.key
                        ? "bg-background text-foreground font-bold shadow-xs border border-border/60"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                    )}
                  >
                    <span>{t(`tabs.${tab.key}`)}</span>
                    {notice === "error" && (
                      <span
                        className="w-2 h-2 rounded-full bg-red-500 shrink-0"
                        title={t("notices.error")}
                      />
                    )}
                    {notice === "warning" && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"
                        title={t("notices.warning")}
                      />
                    )}
                    {notice === "info" && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-blue-500/80 shrink-0"
                        title={t("notices.info")}
                      />
                    )}
                  </button>
                </Fragment>
              );
            })}
          </div>

          <DebugPanelTabContent
            activeTab={activeTab}
            user={user}
            organizations={organizations}
            selectedOrganization={selectedOrganization}
            sessionContext={sessionContext}
            authIsLoading={authIsLoading}
            authError={authError}
            manifest={manifest}
            pathname={pathname}
            overlays={overlays}
          />

          {/* Footer */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-1.5 border-t border-border bg-muted/10 text-sm text-muted-foreground shrink-0">
            <span className="truncate min-w-0 mr-2">
              {sessionContext.label}
            </span>
            <LiveClock />
          </div>
        </div>
      )}
    </div>
  );
  if (!mounted || process.env.NODE_ENV === "production") {
    return null;
  }
  return createPortal(panelContent, document.body);
}
